/**
 * `entities/analysis` query options factories.
 *
 * Thin TanStack `queryOptions` factories wrapping the read API. Consumers
 * compose them with whichever observer fits the call site: `useQuery`,
 * `useSuspenseQuery`, `queryClient.prefetchQuery`, route loaders, etc.
 *
 * Behaviour is ported verbatim from the legacy `features/analysis` hooks:
 *   - `analysesQuery()` polls every 10s while any session is `pending`/`running`
 *     (and stops once none are), with a 5-minute `staleTime`.
 *   - `analysisQuery(id)` is gated on `id` so callers can pass an optional route
 *     param without a guard, with a 5-minute `staleTime`.
 */
import { queryOptions } from '@tanstack/react-query';
import { getAnalyses, getAnalysisById, getStatistaDatasets, resolveAnalysis } from './analysis.api';
import { ANALYSIS_QUERY_KEYS } from './query-keys';

const STALE_TIME = 5 * 60 * 1000; // 5 minutes
const POLL_INTERVAL = 10_000; // 10 seconds while a session is in-flight

/**
 * List of analysis sessions. Polls while any session is still generating so the
 * list reflects completion without a manual refresh.
 */
export const analysesQuery = () =>
  queryOptions({
    queryKey: ANALYSIS_QUERY_KEYS.lists(),
    queryFn: ({ signal }) => getAnalyses(signal),
    staleTime: STALE_TIME,
    refetchInterval: (query) => {
      const data = query.state.data;
      const hasGenerating = data?.some((a) => a.status === 'pending' || a.status === 'running');
      return hasGenerating ? POLL_INTERVAL : false;
    },
  });

type AnalysisQueryOptions = {
  suppressErrorNotification?: boolean;
};

/**
 * Single analysis by id. `enabled` is gated on `id` so the factory can be called
 * eagerly with an optional route param (`undefined` → query stays idle).
 */
export const analysisQuery = (id?: string, options?: AnalysisQueryOptions) =>
  queryOptions({
    // `options` only tunes toast suppression on the request — the fetched data
    // is identical, so it must NOT split the cache bucket.
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ANALYSIS_QUERY_KEYS.detail(id ?? ''),
    queryFn: ({ signal }) =>
      getAnalysisById(id!, {
        signal,
        suppressErrorNotification: options?.suppressErrorNotification,
      }),
    enabled: Boolean(id),
    staleTime: STALE_TIME,
  });

/**
 * Deep-link resolution of an analysis id to its owning workspace. Runs BEFORE
 * the workspace-scoped `analysisQuery` (via `useDeepLinkResolution`) so a deep
 * link from another workspace doesn't 404 under the ambient
 * `x-selected-workspace-id` header.
 */
export const analysisResolutionQuery = (id?: string) =>
  queryOptions({
    queryKey: ANALYSIS_QUERY_KEYS.resolution(id ?? ''),
    queryFn: () => resolveAnalysis(id!),
  });

/**
 * Statista datasets behind the Category & Competition figures. `enabled` takes
 * the caller's gate as well as `id`, so the request is never made while the
 * feature flag is off.
 */
export const statistaDatasetsQuery = (id?: string, { enabled = true } = {}) =>
  queryOptions({
    queryKey: ANALYSIS_QUERY_KEYS.statistaDatasets(id ?? ''),
    queryFn: ({ signal }) => getStatistaDatasets(id!, signal),
    enabled: enabled && Boolean(id),
    staleTime: STALE_TIME,
  });
